import useSWR from 'swr';
import { useMemo } from 'react';

import { useVisibilityState } from 'src/hooks/useVisibilityState';

import axios, { fetcher } from 'src/utils/axios';

class FinanceApi {
  async getBalance(params = {}) {
    return await axios.get('/client/client_account/finance/balance', { params });
  }

  async getWallets(params = {}) {
    return await axios.get('/client/client_account/finance/wallet/wallets', { params });
  }

  async getWithdraw(data = {}) {
    return await axios.post(`/client/client_account/finance/withdraw`, data);
  }

  async sendDeposit(request = {}) {
    return await axios.post('/client/client_account/finance/payment_requests', request);
  }

  async getTransactions(params = {}) {
    return await axios.get('/client/client_account/finance/transactions', { params });
  }

  async addFavoriteTicker(data = {}) {
    return await axios.post('/client/client_account/finance/favorite_tickers', data);
  }

  async removeFavoriteTicker(id) {
    return await axios.delete(`/client/client_account/finance/favorite_tickers/${id}`);
  }

  async getRequiredMargin(params = {}) {
    return await axios.get('/client/client_account/finance/margin', { params });
  }

  async getPositions(params = {}) {
    return await axios.get(`/client/client_account/finance/positions`, { params });
  }

  async createPosition(data) {
    return await axios.post(`/client/client_account/finance/positions`, data);
  }

  async updatePosition(id, data) {
    return await axios.put(`/client/client_account/finance/positions/${id}`, data);
  }

  async copyPosition(data) {
    return await axios.post(`/client/client_account/finance/copy_positions`, data);
  }

  async getTickers(params = {}) {
    return await axios.get("/client/client_account/finance/tickers", { params });
  }

  async getTradingAccounts(params = {}) {
    return await axios.get("/client/client_account/finance/trading_accounts", { params });
  }

  async sendBankDeposit(data) {
    const response = await axios.post("/client/client_account/finance/transactions", data);
    return response;
  }

  async createTradingAccount(data = {}) {
    return await axios.post("/client/client_account/finance/trading_accounts", data);
  }

  async updateTradingAccount(id, data = {}) {
    return await axios.put(`/client/client_account/finance/trading_accounts/${id}`, data);
  }

  async getWDForms(params = {}) {
    return await axios.get("/client/client_account/wd_forms", { params });
  }

  async convertAmount(request = {}) {
    const response = await axios.post(`client/client_account/finance/convert_amount`, request);
    return response;
  }

  async getPaymentProviders(params = {}) {
    const response = await axios.get(`client/psp_providers`, { params });
    return response;
  }

  async createPayment(id, request = {}) {
    const response = await axios.post(`client/psp_providers/${id}/create_payment`, request);
    return response;
  }
}

export const financeApi = new FinanceApi();

export function useGetTickersList(params) {
  const isVisible = useVisibilityState();
  const URL = ["/client/client_account/finance/tickers", { params }] ?? '';

  const { data, isLoading, error, isValidating, mutate } = useSWR(
    isVisible ? URL : null,
    fetcher,
    { refreshInterval: isVisible ? 500 : 0 }
  );

  const memoizedValue = useMemo(
    () => ({
      tickers: data?.tickers || [],
      isLoading,
      error,
      mutate,
      isValidating,
      totalCount: data?.total_count ?? 0,
      totalPage: data?.total_pages ?? 0,
      tickersEmpty: !isLoading && !data?.tickers.length,
    }),
    [data?.tickers, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetBalance() {
  const isVisible = useVisibilityState();
  const URL = "/client/client_account/finance/positions";

  const { data, isLoading, error, isValidating } = useSWR(
    isVisible ? URL : null,
    fetcher,
    { refreshInterval: isVisible ? 2000 : 0 }
  );

  const memoizedValue = useMemo(
    () => ({
      balance: data?.balance,
      isLoading,
      error,
      isValidating,
    }),
    [data?.balance, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetTicker(tickerId, params) {
  const isVisible = useVisibilityState();
  const URL = tickerId ? [`/client/client_account/finance/tickers/${tickerId}`, { params }] ?? '' : "";

  const { data, isLoading, error, isValidating, mutate } = useSWR(
    isVisible ? URL : null,
    fetcher
  );

  const memoizedValue = useMemo(
    () => ({
      ticker: data?.ticker,
      isLoading,
      error,
      mutate,
      isValidating,
    }),
    [data?.ticker, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetPositions(params) {
  const isVisible = useVisibilityState();
  const URL = ["/client/client_account/finance/positions", { params }] ?? '';

  const { data, isLoading, error, isValidating, mutate } = useSWR(
    isVisible ? URL : null,
    fetcher,
    { refreshInterval: isVisible ? 3000 : 0 }
  );

  const memoizedValue = useMemo(
    () => ({
      positions: data?.positions,
      balance: data?.balance,
      totalCount: data?.total_count,
      isLoading,
      error,
      mutate,
      isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}


